import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import {
  addCostCenterMember,
  archiveManualProject,
  clearCostCenterDisplayName,
  decideCostCenterRelationship,
  fetchCostCenterAliases,
  fetchCostCenterAssignments,
  fetchCostCenterLabelCandidates,
  fetchCostCenterRelationshipEvidence,
  fetchCostCenterSourceCoverage,
  fetchCostCenters,
  mergeCostCenterAlias,
  removeCostCenterMember,
  setCostCenterDisplayName,
  undoCostCenterAlias,
  updateManualProjectRole,
} from "../api/costCenters";
import type {
  CostCenterAlias,
  CostCenterAssignment,
  CostCenterAssignmentSuggestion,
  CostCenterAssignmentsResponse,
  CostCenterEntity,
  CostCenterLabelCandidate,
  CostCenterLabelCandidatesResponse,
  CostCenterRelationshipCandidate,
  CostCenterRelationshipEvidence,
  CostCenterType,
  ManualProjectRole,
} from "../api/costCenters";
import { ApiError } from "../lib/api";
import { isDemoMode } from "../lib/env";
import { formatEuro } from "../lib/format";
import {
  costCenterTypeLabel,
  displayCostCenterMeta,
  displayCostCenterRawReference,
  displayCostCenterTitle,
} from "../lib/costCenterDisplay";
import { uiApiErrorMessage } from "../lib/apiErrorLabels";
import {
  coverageRowToCostCenterEntity,
  uniqueCostCenterEntities,
} from "../lib/costCenterCoverageEntity";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { useEscapeKey } from "../lib/useEscapeKey";
import DocumentPdfActions from "./DocumentPdfActions";

export type EditableCostCenter = {
  costCenterType: CostCenterType | string;
  costCenterKey: string;
  displayLabel: string | null;
  sourceDisplayLabel?: string | null;
  manualDisplayLabel?: string | null;
  customerAnchorLabel?: string | null;
  projectRole?: ManualProjectRole | string | null;
};

const EDITABLE_COST_CENTER_TYPES: CostCenterType[] = [
  "project",
  "delivery_address",
  "customer_reference",
  "order_context",
  "manual_cost_center",
];

type CostCenterEditTab = "edit" | "assignments" | "advanced";

export default function CostCenterEditDrawer({
  entity,
  onClose,
}: {
  entity: EditableCostCenter | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [nameNote, setNameNote] = useState("");
  const [savedProjectRole, setSavedProjectRole] =
    useState<ManualProjectRole>("project");
  const [projectRoleDraft, setProjectRoleDraft] =
    useState<ManualProjectRole>("project");
  const [projectRoleNote, setProjectRoleNote] = useState("");
  const [archiveNote, setArchiveNote] = useState("");
  const [activeTab, setActiveTab] = useState<CostCenterEditTab>("edit");
  const [archiveConfirmDialogOpen, setArchiveConfirmDialogOpen] = useState(false);
  const archiveConfirmDialogRef = useRef<HTMLDivElement | null>(null);
  const archiveConfirmCancelRef = useRef<HTMLButtonElement | null>(null);
  const archiveConfirmPreviousFocusRef = useRef<HTMLElement | null>(null);
  const [mergeSearch, setMergeSearch] = useState("");
  const [selectedTarget, setSelectedTarget] = useState<CostCenterEntity | null>(
    null,
  );
  const [mergeNote, setMergeNote] = useState("");
  const [showCrossTypeMergeTargets, setShowCrossTypeMergeTargets] =
    useState(false);
  const debouncedMergeSearch = useDebouncedValue(mergeSearch, 200);
  const trimmedMergeSearch = debouncedMergeSearch.trim();
  const mergeSearchIsSettling =
    mergeSearch.trim().length >= 2 && mergeSearch.trim() !== trimmedMergeSearch;
  const editableType =
    entity && isEditableCostCenterType(entity.costCenterType)
      ? entity.costCenterType
      : null;
  const title = entity
    ? displayCostCenterTitle(
        entity.costCenterType,
        entity.costCenterKey,
        entity.displayLabel,
        {
          customerAnchorLabel: entity.customerAnchorLabel,
          manualLabel: entity.manualDisplayLabel,
        },
      )
    : "";
  const sourceTitle = entity
    ? displayCostCenterTitle(
        entity.costCenterType,
        entity.costCenterKey,
        entity.sourceDisplayLabel ?? entity.displayLabel,
      )
    : "";
  const sourceTitleLabel =
    entity?.costCenterType === "customer_reference" && entity.customerAnchorLabel
      ? "Referenz"
      : "Quelle";

  useEffect(() => {
    if (!entity) return;
    const role = normalizedProjectRole(entity.projectRole);
    setDisplayName(title);
    setNameNote("");
    setSavedProjectRole(role);
    setProjectRoleDraft(role);
    setProjectRoleNote("");
    setArchiveNote("");
    setActiveTab("edit");
    setArchiveConfirmDialogOpen(false);
    setMergeSearch("");
    setSelectedTarget(null);
    setMergeNote("");
    setShowCrossTypeMergeTargets(false);
  }, [entity, title]);

  const aliasesQuery = useQuery({
    queryKey: [
      "cost-center-aliases",
      entity?.costCenterType,
      entity?.costCenterKey,
    ],
    queryFn: () =>
      fetchCostCenterAliases({
        costCenterType: entity!.costCenterType,
        costCenterKey: entity!.costCenterKey,
      }),
    enabled: Boolean(entity),
  });
  const labelCandidatesQuery = useQuery({
    queryKey: [
      "cost-center-label-candidates",
      entity?.costCenterType,
      entity?.costCenterKey,
    ],
    queryFn: () =>
      fetchCostCenterLabelCandidates(
        entity!.costCenterKey,
        entity!.costCenterType,
      ),
    enabled: Boolean(entity) && entity?.costCenterType === "project",
    staleTime: 60_000,
  });
  const sameTypeTargetSearchQuery = useQuery({
    queryKey: [
      "cost-centers",
      "alias-target-search",
      "same-type",
      editableType,
      trimmedMergeSearch,
    ],
    queryFn: () =>
      fetchCostCenters({
        type: editableType!,
        q: trimmedMergeSearch,
        limit: 12,
        sort: "name_asc",
      }),
    enabled: Boolean(editableType) && trimmedMergeSearch.length >= 2,
    staleTime: 30_000,
  });
  const crossTypeTargetSearchQuery = useQuery({
    queryKey: [
      "cost-centers",
      "alias-target-search",
      "cross-type",
      editableType,
      trimmedMergeSearch,
    ],
    queryFn: () =>
      fetchCostCenters({
        q: trimmedMergeSearch,
        limit: 24,
        sort: "name_asc",
      }),
    enabled:
      Boolean(editableType) &&
      showCrossTypeMergeTargets &&
      trimmedMergeSearch.length >= 2,
    staleTime: 30_000,
  });
  const displayNameMutation = useMutation({
    mutationFn: setCostCenterDisplayName,
    onSuccess: () => {
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const clearDisplayNameMutation = useMutation({
    mutationFn: clearCostCenterDisplayName,
    onSuccess: () => {
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const projectRoleMutation = useMutation({
    mutationFn: updateManualProjectRole,
    onSuccess: (response) => {
      const nextRole = normalizedProjectRole(response.item.project_role);
      setSavedProjectRole(nextRole);
      setProjectRoleDraft(nextRole);
      setProjectRoleNote("");
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const archiveMutation = useMutation({
    mutationFn: archiveManualProject,
    onSuccess: () => {
      invalidateCostCenterEditQueries(queryClient);
      onClose();
    },
  });
  const mergeMutation = useMutation({
    mutationFn: mergeCostCenterAlias,
    onSuccess: () => {
      setSelectedTarget(null);
      setMergeSearch("");
      setMergeNote("");
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const undoMutation = useMutation({
    mutationFn: undoCostCenterAlias,
    onSuccess: () => {
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const assignmentsQuery = useQuery({
    queryKey: [
      "cost-center-assignments",
      entity?.costCenterType,
      entity?.costCenterKey,
    ],
    queryFn: () =>
      fetchCostCenterAssignments({
        costCenterType: entity!.costCenterType,
        costCenterKey: entity!.costCenterKey,
      }),
    enabled: Boolean(entity),
    // Stay quiet about a missing/legacy backend; surface network errors as an
    // inline notice instead of a noisy retry storm.
    retry: false,
    staleTime: 30_000,
  });
  const relationshipDecisionMutation = useMutation({
    mutationFn: decideCostCenterRelationship,
    onSuccess: () => {
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const [memberSearch, setMemberSearch] = useState("");
  const [memberCandidate, setMemberCandidate] = useState<CostCenterEntity | null>(
    null,
  );
  const [memberNote, setMemberNote] = useState("");
  const debouncedMemberSearch = useDebouncedValue(memberSearch, 200);
  const trimmedMemberSearch = debouncedMemberSearch.trim();
  const memberSearchIsSettling =
    memberSearch.trim().length >= 2 &&
    memberSearch.trim() !== trimmedMemberSearch;
  const memberSearchQuery = useQuery({
    queryKey: ["cost-centers", "member-target-search", trimmedMemberSearch],
    queryFn: () =>
      fetchCostCenters({
        q: trimmedMemberSearch,
        limit: 10,
        sort: "name_asc",
      }),
    enabled: trimmedMemberSearch.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const memberCustomerReferenceQuery = useQuery({
    queryKey: [
      "cost-center-source-coverage",
      "member-target-search",
      "customer-anchors",
      trimmedMemberSearch,
    ],
    queryFn: () =>
      fetchCostCenterSourceCoverage({
        type: "customer_reference",
        q: trimmedMemberSearch,
        view: "customer_anchor",
        limit: 10,
      }),
    enabled: trimmedMemberSearch.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const addMemberMutation = useMutation({
    mutationFn: addCostCenterMember,
    onSuccess: () => {
      setMemberCandidate(null);
      setMemberSearch("");
      setMemberNote("");
      setArchiveConfirmDialogOpen(false);
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const removeMemberMutation = useMutation({
    mutationFn: removeCostCenterMember,
    onSuccess: () => {
      setArchiveConfirmDialogOpen(false);
      invalidateCostCenterEditQueries(queryClient);
    },
  });
  const memberPickerActive =
    memberSearch.trim().length > 0 || memberCandidate !== null;
  const mergePickerActive =
    mergeSearch.trim().length > 0 ||
    selectedTarget !== null ||
    showCrossTypeMergeTargets;
  useEscapeKey(onClose, Boolean(entity && editableType));
  useEscapeKey(
    () => {
      setMemberSearch("");
      setMemberCandidate(null);
    },
    Boolean(entity && editableType && memberPickerActive),
  );
  useEscapeKey(
    () => {
      setMergeSearch("");
      setSelectedTarget(null);
      setShowCrossTypeMergeTargets(false);
    },
    Boolean(entity && editableType && mergePickerActive),
  );
  useEscapeKey(
    () => {
      if (!isProjectLifecycleMutating) setArchiveConfirmDialogOpen(false);
    },
    Boolean(entity && editableType && archiveConfirmDialogOpen),
  );
  useEffect(() => {
    if (!archiveConfirmDialogOpen) return;
    archiveConfirmPreviousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const focusTimer = window.setTimeout(() => {
      archiveConfirmCancelRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(focusTimer);
      const previousFocus = archiveConfirmPreviousFocusRef.current;
      archiveConfirmPreviousFocusRef.current = null;
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus();
      }
    };
  }, [archiveConfirmDialogOpen]);

  if (!entity || !editableType) return null;

  // Demo-Mode: schreibgeschützt. Selbst falls ein Bearbeiten-Trigger erreicht
  // wird, ist hier kein Editierformular und keine Mutation zugänglich.
  if (isDemoMode()) {
    return (
      <div className="fixed inset-0 z-40">
        <button
          type="button"
          aria-label="Schließen"
          className="absolute inset-0 bg-slate-900/30"
          onClick={onClose}
        />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Bearbeiten im Demo-Modus deaktiviert"
          className="absolute right-0 top-0 flex h-full w-[min(28rem,100vw)] flex-col bg-white shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Schreibgeschützte Demo
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="h-7 w-7 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              ×
            </button>
          </div>
          <div className="p-5 text-sm leading-relaxed text-slate-600">
            Bearbeiten ist in der öffentlichen Demo deaktiviert. Alle Daten sind
            anonymisierte Beispieldaten und können nicht verändert werden.
          </div>
        </aside>
      </div>
    );
  }

  const currentEntity = entity;
  const currentType = editableType;
  const isManualProject =
    currentType === "project" &&
    currentEntity.costCenterKey.startsWith("project:manual:");
  const assignmentRows = assignmentsQuery.data?.assignments ?? [];
  const manualAssignments = assignmentRows.filter(
    (assignment) => assignment.origin === "manual",
  );
  const archiveMemberCheckPending = isManualProject && assignmentsQuery.isLoading;
  const archiveMemberCheckFailed = isManualProject && assignmentsQuery.isError;
  const aliases = aliasesQuery.data?.items ?? [];
  const sameTypeTargetOptions = (
    sameTypeTargetSearchQuery.data?.items ?? []
  ).filter(
    (candidate) =>
      !(
        candidate.cost_center_type === entity.costCenterType &&
        candidate.cost_center_key === entity.costCenterKey
      ),
  );
  const crossTypeTargetOptions = showCrossTypeMergeTargets
    ? (crossTypeTargetSearchQuery.data?.items ?? []).filter(
        (candidate) => candidate.cost_center_type !== entity.costCenterType,
      )
    : [];
  const visibleMergeTargetOptions = showCrossTypeMergeTargets
    ? [...sameTypeTargetOptions, ...crossTypeTargetOptions]
    : sameTypeTargetOptions;
  const mergeHasSearchInput = mergeSearch.trim().length >= 2;
  const mergeSearchLoading =
    mergeHasSearchInput &&
    (mergeSearchIsSettling ||
      sameTypeTargetSearchQuery.isLoading ||
      (showCrossTypeMergeTargets && crossTypeTargetSearchQuery.isLoading));
  const nameError = displayNameMutation.isError
    ? costCenterEditMutationErrorLabel(
        displayNameMutation.error,
        "Anzeigename konnte nicht gespeichert werden.",
      )
    : clearDisplayNameMutation.isError
      ? costCenterEditMutationErrorLabel(
          clearDisplayNameMutation.error,
          "Anzeigename konnte nicht zurückgesetzt werden.",
        )
      : null;
  const aliasError = mergeMutation.isError
    ? costCenterEditMutationErrorLabel(
        mergeMutation.error,
        "Kostenstellen konnten nicht zusammengeführt werden.",
      )
    : undoMutation.isError
      ? costCenterEditMutationErrorLabel(
          undoMutation.error,
          "Zusammenführung konnte nicht aufgehoben werden.",
        )
      : null;
  const projectRoleError = projectRoleMutation.isError
    ? costCenterEditMutationErrorLabel(
        projectRoleMutation.error,
        "Projektrolle konnte nicht gespeichert werden.",
      )
    : null;
  const archiveError = archiveMutation.isError
    ? uiApiErrorMessage(
        archiveMutation.error,
        "Projekt konnte nicht archiviert werden.",
      )
    : null;
  const isNameMutating =
    displayNameMutation.isPending || clearDisplayNameMutation.isPending;
  const isAliasMutating = mergeMutation.isPending || undoMutation.isPending;
  const isProjectLifecycleMutating =
    projectRoleMutation.isPending || archiveMutation.isPending;
  const archiveButtonDisabled =
    isProjectLifecycleMutating ||
    archiveMemberCheckPending ||
    archiveMemberCheckFailed;
  const archiveButtonLabel = archiveMutation.isPending
    ? "Archiviert..."
    : "Projekt archivieren";

  function assignmentRelationshipPartnerKey(
    candidate: CostCenterRelationshipCandidate,
  ): string {
    const isParent =
      candidate.parent_cost_center_type === currentType &&
      candidate.parent_cost_center_key === currentEntity.costCenterKey;
    return isParent
      ? `${candidate.child_cost_center_type}:${candidate.child_cost_center_key}`
      : `${candidate.parent_cost_center_type}:${candidate.parent_cost_center_key}`;
  }

  // Both auto-assignment removal and suggestion promote/dismiss flow through the
  // single relationship-decision mutation; manual removal uses the grouping
  // member mutation. A row is "pending" when its partner key matches either.
  const pendingRowKey =
    removeMemberMutation.isPending && removeMemberMutation.variables
      ? `${removeMemberMutation.variables.memberCostCenterType}:${removeMemberMutation.variables.memberCostCenterKey}`
      : relationshipDecisionMutation.isPending &&
          relationshipDecisionMutation.variables
        ? assignmentRelationshipPartnerKey(
            relationshipDecisionMutation.variables.candidate,
          )
        : null;
  const assignmentRemoveError = removeMemberMutation.isError
    ? costCenterEditMutationErrorLabel(
        removeMemberMutation.error,
        "Zuordnung konnte nicht entfernt werden.",
      )
    : relationshipDecisionMutation.isError
      ? costCenterEditMutationErrorLabel(
          relationshipDecisionMutation.error,
          "Automatische Zuordnung konnte nicht geändert werden.",
        )
      : null;

  function removeAssignment(assignment: CostCenterAssignment) {
    if (assignment.origin === "manual") {
      relationshipDecisionMutation.reset();
      removeMemberMutation.mutate({
        parentCostCenterType: currentType,
        parentCostCenterKey: currentEntity.costCenterKey,
        memberCostCenterType: assignment.cost_center_type,
        memberCostCenterKey: assignment.cost_center_key,
      });
      return;
    }
    removeMemberMutation.reset();
    relationshipDecisionMutation.mutate({
      candidate: assignmentToRelationshipCandidate(assignment),
      action: "reject",
    });
  }

  function decideSuggestion(
    suggestion: CostCenterAssignmentSuggestion,
    action: "confirm" | "reject",
  ) {
    removeMemberMutation.reset();
    relationshipDecisionMutation.mutate({
      candidate: assignmentToRelationshipCandidate(suggestion),
      action,
    });
  }

  function saveDisplayName() {
    if (!displayName.trim() || isNameMutating) return;
    displayNameMutation.reset();
    clearDisplayNameMutation.reset();
    displayNameMutation.mutate({
      costCenterType: currentType,
      costCenterKey: currentEntity.costCenterKey,
      displayName: displayName.trim(),
      note: nameNote.trim() || undefined,
    });
  }

  function clearDisplayName() {
    if (isNameMutating || !currentEntity.manualDisplayLabel) return;
    displayNameMutation.reset();
    clearDisplayNameMutation.reset();
    clearDisplayNameMutation.mutate({
      costCenterType: currentType,
      costCenterKey: currentEntity.costCenterKey,
      note: nameNote.trim() || undefined,
    });
  }

  function applyCandidate(label: string) {
    if (isNameMutating || !label.trim()) return;
    setDisplayName(label);
    setNameNote("");
    displayNameMutation.reset();
    clearDisplayNameMutation.reset();
    displayNameMutation.mutate({
      costCenterType: currentType,
      costCenterKey: currentEntity.costCenterKey,
      displayName: label.trim(),
      note: "Projektbezeichnung aus Quellkandidaten übernommen.",
    });
  }

  function saveProjectRole() {
    if (
      !isManualProject ||
      projectRoleDraft === savedProjectRole ||
      isProjectLifecycleMutating
    ) {
      return;
    }
    projectRoleMutation.reset();
    archiveMutation.reset();
    projectRoleMutation.mutate({
      costCenterType: currentType,
      costCenterKey: currentEntity.costCenterKey,
      projectRole: projectRoleDraft,
      note: projectRoleNote.trim() || undefined,
    });
  }

  function openArchiveConfirmDialog() {
    if (!isManualProject || isProjectLifecycleMutating) return;
    if (archiveMemberCheckPending || archiveMemberCheckFailed) return;
    projectRoleMutation.reset();
    archiveMutation.reset();
    setArchiveConfirmDialogOpen(true);
  }

  function archiveProject() {
    if (!isManualProject || isProjectLifecycleMutating) return;
    if (archiveMemberCheckPending || archiveMemberCheckFailed) return;
    projectRoleMutation.reset();
    archiveMutation.reset();
    archiveMutation.mutate({
      costCenterType: currentType,
      costCenterKey: currentEntity.costCenterKey,
      note: archiveNote.trim() || undefined,
      removeGroupings: manualAssignments.length > 0,
    });
  }

  function handleArchiveConfirmDialogKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
  ) {
    if (event.key !== "Tab") return;
    const dialog = archiveConfirmDialogRef.current;
    if (!dialog) return;
    const focusableElements = Array.from(
      dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }
    const activeElement = document.activeElement;
    const activeIndex =
      activeElement instanceof HTMLElement
        ? focusableElements.indexOf(activeElement)
        : -1;
    const nextIndex = event.shiftKey
      ? activeIndex <= 0
        ? focusableElements.length - 1
        : activeIndex - 1
      : activeIndex === -1 || activeIndex >= focusableElements.length - 1
        ? 0
        : activeIndex + 1;
    event.preventDefault();
    focusableElements[nextIndex]?.focus();
  }

  function mergeIntoTarget() {
    if (!selectedTarget || isAliasMutating) return;
    mergeMutation.reset();
    undoMutation.reset();
    mergeMutation.mutate({
      sourceCostCenterType: currentType,
      sourceCostCenterKey: currentEntity.costCenterKey,
      targetCostCenterType: selectedTarget.cost_center_type,
      targetCostCenterKey: selectedTarget.cost_center_key,
      note: mergeNote.trim() || undefined,
    });
  }

  function undoAlias(alias: CostCenterAlias) {
    if (isAliasMutating) return;
    mergeMutation.reset();
    undoMutation.reset();
    undoMutation.mutate({
      sourceCostCenterType: alias.source_cost_center_type,
      sourceCostCenterKey: alias.source_cost_center_key,
      targetCostCenterType: alias.target_cost_center_type,
      targetCostCenterKey: alias.target_cost_center_key,
      sourceEventId: alias.source_event_id,
      note: mergeNote.trim() || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Bearbeiten schließen"
        className="absolute inset-0 bg-slate-900/30"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Kostenstelle bearbeiten"
        className="absolute right-0 top-0 flex h-full w-[min(44rem,100vw)] flex-col bg-white shadow-2xl"
      >
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-slate-900">
                {title}
              </h2>
              <div className="mt-1 text-sm text-slate-500">
                {costCenterTypeLabel(entity.costCenterType)}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-sm font-medium text-slate-500 hover:bg-slate-100"
            >
              Schließen
            </button>
          </div>
          <div className="mt-4 inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm">
            <button
              type="button"
              aria-pressed={activeTab === "edit"}
              onClick={() => setActiveTab("edit")}
              className={
                activeTab === "edit"
                  ? "rounded bg-emerald-50 px-3 py-1.5 font-medium text-emerald-800"
                  : "rounded px-3 py-1.5 font-medium text-slate-600 hover:text-slate-900"
              }
            >
              Bearbeiten
            </button>
            <button
              type="button"
              aria-pressed={activeTab === "assignments"}
              onClick={() => setActiveTab("assignments")}
              className={
                activeTab === "assignments"
                  ? "rounded bg-emerald-50 px-3 py-1.5 font-medium text-emerald-800"
                  : "rounded px-3 py-1.5 font-medium text-slate-600 hover:text-slate-900"
              }
            >
              Zuordnungen
            </button>
            <button
              type="button"
              aria-pressed={activeTab === "advanced"}
              onClick={() => setActiveTab("advanced")}
              className={
                activeTab === "advanced"
                  ? "rounded bg-emerald-50 px-3 py-1.5 font-medium text-emerald-800"
                  : "rounded px-3 py-1.5 font-medium text-slate-600 hover:text-slate-900"
              }
            >
              Erweitert
            </button>
          </div>
        </div>
        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {activeTab === "edit" ? (
            <>
          <section>
            <h3 className="text-sm font-semibold text-slate-900">Anzeigename</h3>
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                Name
                <input
                  value={displayName}
                  disabled={isNameMutating}
                  onChange={(event) => setDisplayName(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </label>
              <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Notiz optional
                <input
                  value={nameNote}
                  disabled={isNameMutating}
                  onChange={(event) => setNameNote(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </label>
              {entity.manualDisplayLabel &&
              entity.sourceDisplayLabel &&
              entity.manualDisplayLabel !== entity.sourceDisplayLabel ? (
                <div className="mt-2 text-xs text-slate-500">
                  {sourceTitleLabel}: {entity.sourceDisplayLabel}
                </div>
              ) : sourceTitle !== title ? (
                <div className="mt-2 text-xs text-slate-500">
                  {sourceTitleLabel}: {sourceTitle}
                </div>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={isNameMutating || !displayName.trim()}
                  onClick={saveDisplayName}
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
                >
                  {displayNameMutation.isPending ? "Speichert..." : "Speichern"}
                </button>
                <button
                  type="button"
                  disabled={isNameMutating || !entity.manualDisplayLabel}
                  onClick={clearDisplayName}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-default disabled:text-slate-400"
                >
                  Zur Quelle zurücksetzen
                </button>
              </div>
              {nameError ? (
                <div className="mt-2 text-xs text-rose-600">{nameError}</div>
              ) : null}
            </div>
            {currentType === "project" ? (
              <LabelCandidatesPicker
                query={labelCandidatesQuery}
                currentName={displayName.trim()}
                onApply={applyCandidate}
                disabled={isNameMutating}
                pendingLabel={
                  displayNameMutation.isPending
                    ? displayNameMutation.variables?.displayName ?? null
                    : null
                }
              />
            ) : null}
          </section>

          {isManualProject ? (
            <section>
              <h3 className="text-sm font-semibold text-slate-900">
                Projektverwaltung
              </h3>
              <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Projektrolle
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <ProjectRoleChoice
                    value="project"
                    selected={projectRoleDraft === "project"}
                    onSelect={setProjectRoleDraft}
                    disabled={isProjectLifecycleMutating}
                    title="Projekt"
                    description="Für operative Projektkostenstellen mit eigenen Belegwerten und optionalen Zuordnungen."
                  />
                  <ProjectRoleChoice
                    value="reporting_group"
                    selected={projectRoleDraft === "reporting_group"}
                    onSelect={setProjectRoleDraft}
                    disabled={isProjectLifecycleMutating}
                    title="Sammelprojekt"
                    description="Für zusätzliche Auswertungen wie ÖWG-Projekte ohne eigene Quellen-Belege."
                  />
                </div>
                <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Notiz optional
                  <input
                    value={projectRoleNote}
                    disabled={isProjectLifecycleMutating}
                    onChange={(event) => setProjectRoleNote(event.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={
                      isProjectLifecycleMutating ||
                      projectRoleDraft === savedProjectRole
                    }
                    onClick={saveProjectRole}
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
                  >
                    {projectRoleMutation.isPending
                      ? "Speichert..."
                      : "Rolle speichern"}
                  </button>
                </div>
                {projectRoleError ? (
                  <div className="mt-2 text-xs text-rose-600">
                    {projectRoleError}
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

            </>
          ) : activeTab === "assignments" ? (
          <AssignmentsSection
            entityType={currentType}
            entityKey={currentEntity.costCenterKey}
            assignmentsQuery={assignmentsQuery}
            onRemoveAssignment={removeAssignment}
            onPromoteSuggestion={(suggestion) =>
              decideSuggestion(suggestion, "confirm")
            }
            onDismissSuggestion={(suggestion) =>
              decideSuggestion(suggestion, "reject")
            }
            pendingRowKey={pendingRowKey}
            removeError={assignmentRemoveError}
            memberSearch={memberSearch}
            onMemberSearchChange={(value) => {
              setMemberSearch(value);
              setMemberCandidate(null);
            }}
            searchResults={uniqueCostCenterEntities([
              ...(memberCustomerReferenceQuery.data?.items ?? []).map(
                coverageRowToCostCenterEntity,
              ),
              ...(memberSearchQuery.data?.items ?? []).filter(
                (candidate) => candidate.cost_center_type !== "customer_reference",
              ),
            ])}
            searchLoading={
              memberSearch.trim().length >= 2 &&
              (memberSearchQuery.isLoading ||
                memberCustomerReferenceQuery.isLoading ||
                memberSearchIsSettling)
            }
            memberCandidate={memberCandidate}
            onSelectCandidate={(candidate) => {
              setMemberCandidate(candidate);
              setMemberSearch(displayCostCenterTitle(
                candidate.cost_center_type,
                candidate.cost_center_key,
                candidate.display_label,
                {
                  customerAnchorLabel: candidate.customer_anchor_label,
                  manualLabel: candidate.manual_display_label,
                },
              ));
            }}
            memberNote={memberNote}
            onMemberNoteChange={setMemberNote}
            isAdding={addMemberMutation.isPending}
            addError={
              addMemberMutation.isError
                ? costCenterEditMutationErrorLabel(
                    addMemberMutation.error,
                    "Zuordnung konnte nicht hinzugefügt werden.",
                  )
                : null
            }
            onAdd={() => {
              if (!memberCandidate) return;
              addMemberMutation.mutate({
                parentCostCenterType: currentType,
                parentCostCenterKey: currentEntity.costCenterKey,
                memberCostCenterType: memberCandidate.cost_center_type,
                memberCostCenterKey: memberCandidate.cost_center_key,
                note: memberNote.trim() || undefined,
              });
            }}
          />
          ) : (
            <>
          <section>
            <h3 className="text-sm font-semibold text-slate-900">
              Quelle & Abdeckung
            </h3>
            <div className="mt-3 rounded-md border border-slate-200 bg-white p-3 text-sm">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {sourceTitleLabel}
              </div>
              <div className="mt-1 truncate font-medium text-slate-900" title={sourceTitle}>
                {sourceTitle || title}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Belege bleiben Quellenwahrheit; Änderungen hier sind
                Auswertungs-Overlays.
              </div>
            </div>
          </section>

          {isManualProject ? (
            <section>
              <h3 className="text-sm font-semibold text-slate-900">
                Projekt archivieren
              </h3>
              <div className="mt-3 rounded-md border border-rose-100 bg-rose-50 p-3">
                <p className="text-xs text-rose-700/80">
                  Blendet das manuelle Projekt aus. Quellen-Belege bleiben
                  erhalten.
                </p>
                {archiveMemberCheckPending ? (
                  <div className="mt-3 rounded-md border border-rose-200 bg-white px-3 py-2 text-xs text-rose-700">
                    Zugeordnete Kostenstellen werden geprüft...
                  </div>
                ) : manualAssignments.length > 0 ? (
                  <div className="mt-3 rounded-md border border-rose-200 bg-white px-3 py-2 text-xs text-rose-800">
                    {manualAssignments.length}{" "}
                    {manualAssignments.length === 1 ? "Zuordnung" : "Zuordnungen"}{" "}
                    werden beim Archivieren gelöst.
                  </div>
                ) : archiveMemberCheckFailed ? (
                  <div className="mt-3 rounded-md border border-rose-200 bg-white px-3 py-2 text-xs text-rose-800">
                    Zuordnungen konnten nicht geladen werden.
                  </div>
                ) : null}
                <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-rose-700">
                  Notiz optional
                  <input
                    value={archiveNote}
                    disabled={isProjectLifecycleMutating}
                    onChange={(event) => setArchiveNote(event.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-rose-100 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-500/20 disabled:bg-rose-50 disabled:text-slate-400"
                  />
                </label>
                <button
                  type="button"
                  disabled={archiveButtonDisabled}
                  onClick={openArchiveConfirmDialog}
                  className="mt-3 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
                >
                  {archiveMemberCheckPending
                    ? "Prüft..."
                    : archiveMemberCheckFailed
                      ? "Nicht verfügbar"
                      : archiveButtonLabel}
                </button>
                {archiveError ? (
                  <div className="mt-2 text-xs text-rose-600">{archiveError}</div>
                ) : null}
              </div>
            </section>
          ) : null}

          <section>
            <h3 className="text-sm font-semibold text-slate-900">
              Dubletten zusammenführen
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Nur wenn zwei Einträge dieselbe reale Kostenstelle meinen.
            </p>
            <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
              <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
                Ziel-Kostenstelle
                <input
                  value={mergeSearch}
                  disabled={isAliasMutating}
                  onChange={(event) => {
                    setMergeSearch(event.target.value);
                    setSelectedTarget(null);
                    setShowCrossTypeMergeTargets(false);
                  }}
                  placeholder="Name, Adresse, Referenz..."
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </label>
              {selectedTarget ? (
                <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                  Ausgewählt: {costCenterEntityTitle(selectedTarget)}
                </div>
              ) : mergeHasSearchInput ? (
                <div className="mt-2 h-56 overflow-y-auto rounded-md border border-slate-200">
                  {mergeSearchIsSettling ||
                  (mergeSearchLoading && visibleMergeTargetOptions.length === 0) ? (
                    <div className="px-3 py-2 text-sm text-slate-500">
                      Lädt...
                    </div>
                  ) : visibleMergeTargetOptions.length > 0 ? (
                    <>
                      {visibleMergeTargetOptions.map((candidate) => (
                        <button
                          key={`${candidate.cost_center_type}:${candidate.cost_center_key}`}
                          type="button"
                          onClick={() => {
                            setSelectedTarget(candidate);
                            setMergeSearch(costCenterEntityTitle(candidate));
                          }}
                          className="block w-full border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                        >
                          <div className="truncate text-sm font-medium text-slate-900">
                            {costCenterEntityTitle(candidate)}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {costCenterEntityMeta(candidate)}
                          </div>
                        </button>
                      ))}
                      {!showCrossTypeMergeTargets ? (
                        <button
                          type="button"
                          onClick={() => setShowCrossTypeMergeTargets(true)}
                          className="block w-full px-3 py-2 text-left text-xs font-medium text-slate-600 hover:bg-slate-50"
                        >
                          Andere Typen anzeigen
                        </button>
                      ) : crossTypeTargetSearchQuery.isLoading ? (
                        <div className="px-3 py-2 text-xs text-slate-500">
                          Lädt andere Typen...
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-sm text-slate-500">
                      {showCrossTypeMergeTargets
                        ? "Keine Treffer."
                        : `Keine Treffer vom Typ ${costCenterTypeLabel(
                            entity.costCenterType,
                          )}.`}
                      {!showCrossTypeMergeTargets ? (
                        <button
                          type="button"
                          onClick={() => setShowCrossTypeMergeTargets(true)}
                          className="mt-2 block text-xs font-medium text-slate-700 underline underline-offset-2"
                        >
                          Andere Typen anzeigen
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : null}
              <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Notiz optional
                <input
                  value={mergeNote}
                  disabled={isAliasMutating}
                  onChange={(event) => setMergeNote(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </label>
              <div className="mt-3">
                <button
                  type="button"
                  disabled={!selectedTarget || isAliasMutating}
                  onClick={mergeIntoTarget}
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
                >
                  {mergeMutation.isPending
                    ? "Führt zusammen..."
                    : "Zusammenführen"}
                </button>
              </div>
              {aliasError ? (
                <div className="mt-2 text-xs text-rose-600">{aliasError}</div>
              ) : null}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-900">
              Aktive Zusammenführungen
            </h3>
            <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
              {aliasesQuery.isLoading || aliasesQuery.isFetching ? (
                <div className="px-3 py-2 text-sm text-slate-500">Lädt...</div>
              ) : aliases.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">
                  Keine aktiven Zusammenführungen.
                </div>
              ) : (
                aliases.map((alias) => {
                  const currentIsSource =
                    alias.source_cost_center_type === entity.costCenterType &&
                    alias.source_cost_center_key === entity.costCenterKey;
                  return (
                    <div
                      key={`${alias.source_cost_center_type}:${alias.source_cost_center_key}`}
                      className="border-b border-slate-100 px-3 py-2 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-slate-900">
                        {alias.source_display_label || alias.source_cost_center_key}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        Ziel:{" "}
                        {alias.target_display_label || alias.target_cost_center_key}
                      </div>
                      <button
                        type="button"
                        disabled={isAliasMutating}
                        onClick={() => undoAlias(alias)}
                        className="mt-2 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-default disabled:text-slate-400"
                      >
                        {undoMutation.isPending
                          ? "Hebt auf..."
                          : currentIsSource
                            ? "Zusammenführung aufheben"
                            : "Quelle lösen"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </section>
            </>
          )}
        </div>
      </aside>
      {archiveConfirmDialogOpen ? (
        <div
          ref={archiveConfirmDialogRef}
          className="absolute inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="archive-project-confirm-title"
          aria-describedby="archive-project-confirm-description"
          onKeyDown={handleArchiveConfirmDialogKeyDown}
        >
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-2xl">
            <h3
              id="archive-project-confirm-title"
              className="text-base font-semibold text-slate-900"
            >
              Projekt wirklich archivieren?
            </h3>
            <p
              id="archive-project-confirm-description"
              className="mt-2 text-sm text-slate-600"
            >
              Das Projekt wird aus der aktiven Kostenstellenliste ausgeblendet.
              Quellen-Belege und die zugeordneten Kostenstellen bleiben
              unverändert.
            </p>
            {manualAssignments.length > 0 ? (
              <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                <div className="font-medium">
                  Beim Archivieren werden {manualAssignments.length}{" "}
                  {manualAssignments.length === 1
                    ? "Zuordnung"
                    : "Zuordnungen"}{" "}
                  gelöst:
                </div>
                <ul className="mt-2 space-y-1 text-xs">
                  {manualAssignments.slice(0, 8).map((member) => (
                    <li
                      key={`${member.cost_center_type}:${member.cost_center_key}`}
                      className="truncate"
                    >
                      {displayCostCenterTitle(
                        member.cost_center_type,
                        member.cost_center_key,
                        member.display_label,
                      )}{" "}
                      <span className="text-rose-700">
                        ({costCenterTypeLabel(member.cost_center_type)})
                      </span>
                    </li>
                  ))}
                </ul>
                {manualAssignments.length > 8 ? (
                  <div className="mt-1 text-xs text-rose-800">
                    und {manualAssignments.length - 8} weitere.
                  </div>
                ) : null}
              </div>
            ) : null}
            {archiveNote.trim() ? (
              <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Notiz: {archiveNote.trim()}
              </div>
            ) : null}
            {archiveError ? (
              <div className="mt-3 text-sm text-rose-600">{archiveError}</div>
            ) : null}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                ref={archiveConfirmCancelRef}
                type="button"
                disabled={isProjectLifecycleMutating}
                onClick={() => setArchiveConfirmDialogOpen(false)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={isProjectLifecycleMutating}
                onClick={archiveProject}
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-800 hover:bg-rose-100 disabled:cursor-default disabled:opacity-60"
              >
                {archiveMutation.isPending
                  ? "Archiviert..."
                  : "Projekt archivieren"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function isEditableCostCenterType(value: string): value is CostCenterType {
  return EDITABLE_COST_CENTER_TYPES.some((type) => type === value);
}

function normalizedProjectRole(value: unknown): ManualProjectRole {
  return value === "reporting_group" ? "reporting_group" : "project";
}

function ProjectRoleChoice({
  value,
  selected,
  onSelect,
  disabled,
  title,
  description,
}: {
  value: ManualProjectRole;
  selected: boolean;
  onSelect: (value: ManualProjectRole) => void;
  disabled: boolean;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelect(value)}
      className={`rounded-md border px-3 py-2 text-left transition disabled:cursor-default disabled:opacity-60 ${
        selected
          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <span className="block text-sm font-semibold">{title}</span>
      <span className="mt-1 block text-xs leading-5 text-slate-500">
        {description}
      </span>
    </button>
  );
}

function labelCandidateStatus(candidate: CostCenterLabelCandidate): {
  label: string;
  className: string;
} {
  if (candidate.is_auto_label) {
    if (candidate.auto_label_status === "auto_suggested") {
      return { label: "Auto-Vorschlag", className: "bg-amber-50 text-amber-700" };
    }
    return { label: "Auto", className: "bg-emerald-50 text-emerald-700" };
  }
  if (candidate.rejection_reason === "source_deferred") {
    return { label: "Kandidat", className: "bg-amber-50 text-amber-700" };
  }
  if (candidate.is_eligible) {
    return { label: "Sicher", className: "bg-emerald-50 text-emerald-700" };
  }
  return { label: "Vorschlag", className: "bg-slate-100 text-slate-600" };
}

function LabelCandidatesPicker({
  query,
  currentName,
  onApply,
  disabled,
  pendingLabel,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    data?: CostCenterLabelCandidatesResponse;
  };
  currentName: string;
  onApply: (label: string) => void;
  disabled: boolean;
  pendingLabel: string | null;
}) {
  const candidates = query.data?.candidates ?? [];
  if (query.isLoading) {
    return (
      <p className="mt-3 text-xs text-slate-500">Vorschläge werden geladen ...</p>
    );
  }
  if (query.isError || candidates.length === 0) {
    return null;
  }
  return (
    <div className="mt-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Vorschläge aus Belegen
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Aus PDF-/UGL-Belegen erkannte Bezeichnungen. Übernehmen setzt den
        Anzeigenamen; die Quelle bleibt unverändert.
      </p>
      <div className="mt-2 overflow-hidden rounded-md border border-slate-200">
        {candidates.map((candidate) => {
          const status = labelCandidateStatus(candidate);
          const isCurrent = candidate.label === currentName;
          const isApplying = pendingLabel === candidate.label;
          return (
            <div
              key={`${candidate.source_kind}:${candidate.label}`}
              className="flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {candidate.label}
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${status.className}`}
                  >
                    {status.label}
                  </span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                    {candidate.source_kind}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                  <span>{candidate.document_count} Belege</span>
                  <span>{candidate.code_document_count} mit Projektnummer</span>
                </div>
              </div>
              {isCurrent ? (
                <span className="flex-none self-center text-xs font-medium text-emerald-700">
                  Aktiv
                </span>
              ) : (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onApply(candidate.label)}
                  className="flex-none self-center rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
                >
                  {isApplying ? "Speichert ..." : "Übernehmen"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function invalidateCostCenterEditQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-label-candidates"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-source-coverage"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-relationships"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-manual-events"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-aliases"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-members"] });
  void queryClient.invalidateQueries({ queryKey: ["cost-center-assignments"] });
}

function costCenterEditMutationErrorLabel(
  error: unknown,
  fallback: string,
): string {
  return uiApiErrorMessage(error, fallback);
}

function costCenterEntityTitle(entity: CostCenterEntity): string {
  return displayCostCenterTitle(
    entity.cost_center_type,
    entity.cost_center_key,
    entity.display_label,
    {
      customerAnchorLabel: entity.customer_anchor_label,
      manualLabel: entity.manual_display_label,
    },
  );
}

function costCenterEntityRawReference(entity: CostCenterEntity): string | null {
  return displayCostCenterRawReference(
    entity.cost_center_type,
    entity.cost_center_key,
    entity.source_display_label ?? entity.display_label,
  );
}

function costCenterEntityMeta(entity: CostCenterEntity): string {
  const title = costCenterEntityTitle(entity);
  const rawReference = costCenterEntityRawReference(entity);
  if (rawReference && rawReference !== title) {
    return `Kundenreferenz · ${rawReference}`;
  }
  const meta = displayCostCenterMeta(entity.cost_center_type, entity.cost_center_key, title);
  return [costCenterTypeLabel(entity.cost_center_type), meta]
    .filter(Boolean)
    .join(" · ");
}

type AssignmentsQueryShape = {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data?: CostCenterAssignmentsResponse;
};

// Auto assignments and suggestions carry relationship identity; rebuild the
// minimal candidate the existing decision/evidence endpoints expect.
function assignmentToRelationshipCandidate(
  row: CostCenterAssignment | CostCenterAssignmentSuggestion,
): CostCenterRelationshipCandidate {
  return {
    parent_cost_center_key: row.parent_cost_center_key ?? "",
    parent_cost_center_type: row.parent_cost_center_type ?? "",
    parent_display_label: null,
    child_cost_center_key: row.child_cost_center_key ?? "",
    child_cost_center_type: row.child_cost_center_type ?? "",
    child_display_label: null,
    relationship_type: row.relationship_type ?? "",
    state: row.state ?? "",
    confidence: null,
    reason: row.reason ?? null,
    evidence_count: null,
    document_count:
      typeof row.document_count === "number" ? row.document_count : null,
    line_count: null,
    score: row.score ?? null,
    total_spend: row.spend_last_year ?? null,
    first_observed_date: null,
    last_observed_date: null,
    source_kinds: null,
    source_summary: row.source_summary ?? null,
  };
}

function SelectedMemberCandidate({ entity }: { entity: CostCenterEntity }) {
  const title = costCenterEntityTitle(entity);
  const rawReference = costCenterEntityRawReference(entity);
  return (
    <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
      <div className="truncate">Ausgewählt: {title}</div>
      {rawReference && rawReference !== title ? (
        <div className="mt-0.5 truncate text-xs text-emerald-700">
          Referenz: {rawReference}
        </div>
      ) : null}
    </div>
  );
}

function isCostCenterGroupingUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const message = error.message.toLowerCase();
  if (error.status === 404 && message === "not found") return true;
  return (
    error.status === 503 &&
    (message === "cost_center_groupings_require_postgres" ||
      message.includes("postgres_url fehlt") ||
      message.includes("postgres-read-backend"))
  );
}

function AssignmentsSection({
  entityType,
  entityKey,
  assignmentsQuery,
  onRemoveAssignment,
  onPromoteSuggestion,
  onDismissSuggestion,
  pendingRowKey,
  removeError,
  memberSearch,
  onMemberSearchChange,
  searchResults,
  searchLoading,
  memberCandidate,
  onSelectCandidate,
  memberNote,
  onMemberNoteChange,
  isAdding,
  addError,
  onAdd,
}: {
  entityType: string;
  entityKey: string;
  assignmentsQuery: AssignmentsQueryShape;
  onRemoveAssignment: (assignment: CostCenterAssignment) => void;
  onPromoteSuggestion: (suggestion: CostCenterAssignmentSuggestion) => void;
  onDismissSuggestion: (suggestion: CostCenterAssignmentSuggestion) => void;
  pendingRowKey: string | null;
  removeError: string | null;
  memberSearch: string;
  onMemberSearchChange: (value: string) => void;
  searchResults: CostCenterEntity[];
  searchLoading: boolean;
  memberCandidate: CostCenterEntity | null;
  onSelectCandidate: (candidate: CostCenterEntity) => void;
  memberNote: string;
  onMemberNoteChange: (value: string) => void;
  isAdding: boolean;
  addError: string | null;
  onAdd: () => void;
}) {
  const data = assignmentsQuery.data;
  const assignments = data?.assignments ?? [];
  const suggestions = data?.suggestions ?? [];
  const assignedKeys = new Set(
    assignments.map((a) => `${a.cost_center_type}:${a.cost_center_key}`),
  );
  const filteredCandidates = searchResults.filter((candidate) => {
    const key = `${candidate.cost_center_type}:${candidate.cost_center_key}`;
    if (key === `${entityType}:${entityKey}`) return false;
    if (assignedKeys.has(key)) return false;
    if (
      candidate.cost_center_type === "project" &&
      candidate.project_role === "reporting_group"
    ) {
      return false;
    }
    return true;
  });
  const groupingUnavailable =
    assignmentsQuery.isError &&
    isCostCenterGroupingUnavailable(assignmentsQuery.error);
  const hasSearchInput = memberSearch.trim().length >= 2;

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Zugeordnete Kostenstellen
        </h3>
        <AssignmentAggregates aggregates={data?.aggregates} />
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Automatische und manuell ergänzte Zuordnungen. Quellen-Belege bleiben
        unverändert.
      </p>
      {!groupingUnavailable ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
          <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
            Kostenstelle zuordnen
            <input
              value={memberSearch}
              disabled={isAdding}
              onChange={(event) => onMemberSearchChange(event.target.value)}
              placeholder="Name, Adresse, Referenz..."
              className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
          {memberCandidate ? (
            <SelectedMemberCandidate entity={memberCandidate} />
          ) : hasSearchInput ? (
            <div className="mt-2 h-56 overflow-y-auto rounded-md border border-slate-200">
              {searchLoading && filteredCandidates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">Lädt...</div>
              ) : filteredCandidates.length > 0 ? (
                filteredCandidates.map((candidate) => {
                  const title = costCenterEntityTitle(candidate);
                  const rawReference = costCenterEntityRawReference(candidate);
                  return (
                    <button
                      key={`${candidate.cost_center_type}:${candidate.cost_center_key}`}
                      type="button"
                      onClick={() => onSelectCandidate(candidate)}
                      className="block w-full border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50"
                    >
                      <div className="truncate text-sm font-medium text-slate-900">
                        {title}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500">
                        {rawReference && rawReference !== title
                          ? `Referenz: ${rawReference}`
                          : costCenterTypeLabel(candidate.cost_center_type)}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-slate-500">
                  Keine passende Kostenstelle gefunden.
                </div>
              )}
            </div>
          ) : null}
          <label className="mt-3 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Notiz optional
            <input
              value={memberNote}
              disabled={isAdding}
              onChange={(event) => onMemberNoteChange(event.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
          <div className="mt-3">
            <button
              type="button"
              disabled={!memberCandidate || isAdding}
              onClick={onAdd}
              className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
            >
              {isAdding ? "Fügt hinzu..." : "Zuordnung hinzufügen"}
            </button>
          </div>
          {addError ? (
            <div className="mt-2 text-xs text-rose-600">{addError}</div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-3 overflow-hidden rounded-md border border-slate-200">
        {assignmentsQuery.isLoading ? (
          <div className="px-3 py-2 text-sm text-slate-500">Lädt...</div>
        ) : groupingUnavailable ? (
          <div className="px-3 py-2 text-sm text-slate-500">
            Zuordnungen sind in dieser Backend-Konfiguration noch nicht aktiv.
          </div>
        ) : assignmentsQuery.isError ? (
          <div className="px-3 py-2 text-sm text-rose-600">
            Zuordnungen konnten nicht geladen werden.
          </div>
        ) : assignments.length === 0 ? (
          <div className="px-3 py-2 text-sm text-slate-500">
            Noch keine Kostenstellen zugeordnet.
          </div>
        ) : (
          assignments.map((assignment) => {
            const key = `${assignment.cost_center_type}:${assignment.cost_center_key}`;
            return (
              <AssignmentRow
                key={`${assignment.origin}:${key}`}
                assignment={assignment}
                pending={pendingRowKey === key}
                onRemove={() => onRemoveAssignment(assignment)}
              />
            );
          })
        )}
      </div>
      {removeError ? (
        <div className="mt-2 text-xs text-rose-600">{removeError}</div>
      ) : null}
      {suggestions.length > 0 ? (
        <SuggestionsDisclosure
          suggestions={suggestions}
          pendingRowKey={pendingRowKey}
          onPromote={onPromoteSuggestion}
          onDismiss={onDismissSuggestion}
        />
      ) : null}
    </section>
  );
}

function AssignmentAggregates({
  aggregates,
}: {
  aggregates: CostCenterAssignmentsResponse["aggregates"] | undefined;
}) {
  const [open, setOpen] = useState(false);
  if (!aggregates) return null;
  return (
    <div className="text-right text-xs text-slate-500">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-slate-100"
      >
        <span>Auswertung</span>
        <span className="font-semibold tabular-nums text-slate-800">
          {formatEuro(aggregates.evaluation_spend_last_year)}
        </span>
        <span aria-hidden="true" className="text-slate-400">
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open ? (
        <div className="mt-1 flex flex-wrap justify-end gap-x-3">
          <span>
            Eigenwert{" "}
            <span className="font-medium tabular-nums text-slate-700">
              {formatEuro(aggregates.own_spend_last_year)}
            </span>
          </span>
          <span>
            Zugeordnet{" "}
            <span className="font-medium tabular-nums text-slate-700">
              {formatEuro(aggregates.assigned_spend_last_year)}
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function AssignmentOriginBadge({ origin }: { origin: CostCenterAssignment["origin"] }) {
  const isAuto = origin === "auto";
  return (
    <span
      className={`mt-0.5 flex-none rounded px-1.5 py-0.5 text-[11px] font-medium ${
        isAuto
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      {isAuto ? "Auto" : "Manuell"}
    </span>
  );
}

function AssignmentRow({
  assignment,
  pending,
  onRemove,
}: {
  assignment: CostCenterAssignment;
  pending: boolean;
  onRemove: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const isAuto = assignment.origin === "auto";
  const title = displayCostCenterTitle(
    assignment.cost_center_type,
    assignment.cost_center_key,
    assignment.display_label,
    { manualLabel: assignment.manual_display_label },
  );
  const meta = [
    costCenterTypeLabel(assignment.cost_center_type),
    isAuto && assignment.score != null ? `${assignment.score} %` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="border-b border-slate-100 px-3 py-2.5 last:border-b-0">
      <div className="flex items-start gap-3">
        <AssignmentOriginBadge origin={assignment.origin} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-900" title={title}>
            {title}
          </div>
          <div className="mt-0.5 text-xs text-slate-500">{meta}</div>
        </div>
        <div className="flex flex-none items-center gap-1.5">
          {isAuto ? (
            <button
              type="button"
              aria-expanded={showDetails}
              onClick={() => setShowDetails((current) => !current)}
              className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              {showDetails ? "Details ▾" : "Details ▸"}
            </button>
          ) : null}
          <button
            type="button"
            disabled={pending}
            onClick={onRemove}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
          >
            {pending ? "Entfernt..." : "Entfernen"}
          </button>
        </div>
      </div>
      {isAuto && showDetails ? (
        <RelationshipDetails
          candidate={assignmentToRelationshipCandidate(assignment)}
          reason={assignment.reason ?? null}
          sourceSummary={assignment.source_summary ?? null}
        />
      ) : null}
    </div>
  );
}

function SuggestionsDisclosure({
  suggestions,
  pendingRowKey,
  onPromote,
  onDismiss,
}: {
  suggestions: CostCenterAssignmentSuggestion[];
  pendingRowKey: string | null;
  onPromote: (suggestion: CostCenterAssignmentSuggestion) => void;
  onDismiss: (suggestion: CostCenterAssignmentSuggestion) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = `${suggestions.length} ${
    suggestions.length === 1 ? "mögliche Zuordnung" : "mögliche Zuordnungen"
  } prüfen`;
  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
      >
        <span aria-hidden="true" className="text-slate-400">
          {open ? "▾" : "▸"}
        </span>
        {label}
      </button>
      {open ? (
        <div className="mt-2 overflow-hidden rounded-md border border-slate-200 bg-white">
          {suggestions.map((suggestion) => {
            const key = `${suggestion.cost_center_type}:${suggestion.cost_center_key}`;
            return (
              <SuggestionRow
                key={key}
                suggestion={suggestion}
                pending={pendingRowKey === key}
                onPromote={() => onPromote(suggestion)}
                onDismiss={() => onDismiss(suggestion)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SuggestionRow({
  suggestion,
  pending,
  onPromote,
  onDismiss,
}: {
  suggestion: CostCenterAssignmentSuggestion;
  pending: boolean;
  onPromote: () => void;
  onDismiss: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const title = displayCostCenterTitle(
    suggestion.cost_center_type,
    suggestion.cost_center_key,
    suggestion.display_label,
    { manualLabel: suggestion.manual_display_label },
  );
  const meta = [
    costCenterTypeLabel(suggestion.cost_center_type),
    suggestion.score != null ? `${suggestion.score} %` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="border-b border-slate-100 px-3 py-2.5 last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-900" title={title}>
            {title}
          </div>
          <div className="mt-0.5 text-xs text-slate-500">{meta}</div>
        </div>
        <div className="flex flex-none flex-wrap items-center justify-end gap-1.5">
          <button
            type="button"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((current) => !current)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            {showDetails ? "Details ▾" : "Details ▸"}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onPromote}
            className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:opacity-60"
          >
            Übernehmen
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={onDismiss}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
          >
            Verwerfen
          </button>
        </div>
      </div>
      {showDetails ? (
        <RelationshipDetails
          candidate={assignmentToRelationshipCandidate(suggestion)}
          reason={suggestion.reason ?? null}
          sourceSummary={suggestion.source_summary ?? null}
        />
      ) : null}
    </div>
  );
}

function RelationshipDetails({
  candidate,
  reason,
  sourceSummary,
}: {
  candidate: CostCenterRelationshipCandidate;
  reason: string | null;
  sourceSummary: string | null;
}) {
  const evidenceQuery = useQuery({
    queryKey: ["cost-center-relationship-evidence", candidateKey(candidate)],
    queryFn: () => fetchCostCenterRelationshipEvidence({ candidate, limit: 8 }),
    staleTime: 30_000,
  });
  const summary = [reason, sourceSummary].filter(Boolean).join(" · ");
  return (
    <div className="mt-2 border-t border-slate-100 pt-2 pl-8">
      {summary ? (
        <div className="text-xs text-slate-500" title={summary}>
          {summary}
        </div>
      ) : null}
      <SuggestionEvidencePanel
        query={{
          isLoading: evidenceQuery.isLoading || evidenceQuery.isFetching,
          isError: evidenceQuery.isError,
          items: evidenceQuery.data?.items ?? [],
        }}
      />
    </div>
  );
}

function candidateKey(candidate: CostCenterRelationshipCandidate): string {
  return [
    candidate.relationship_type,
    candidate.parent_cost_center_type,
    candidate.parent_cost_center_key,
    candidate.child_cost_center_type,
    candidate.child_cost_center_key,
  ].join("|");
}

function SuggestionEvidencePanel({
  query,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    items: CostCenterRelationshipEvidence[];
  };
}) {
  if (query.isLoading) {
    return <div className="mt-2 text-xs text-slate-500">Lade Nachweise …</div>;
  }
  if (query.isError) {
    return (
      <div className="mt-2 text-xs text-rose-600">
        Nachweise konnten nicht geladen werden.
      </div>
    );
  }
  if (query.items.length === 0) {
    return <div className="mt-2 text-xs text-slate-500">Keine Nachweise gefunden.</div>;
  }
  return (
    <div className="mt-2 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
      {query.items.map((item) => (
        <div
          key={[
            item.source_kind,
            item.source_doc_id,
            item.source_line_pos,
            item.source_summary,
          ].join("|")}
          className="border-b border-slate-200 px-3 py-2 last:border-b-0"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="min-w-0">
              <DocumentPdfActions
                docId={item.source_doc_id}
                docNumber={item.source_doc_number}
                sourceKind={item.source_kind}
                linePos={item.source_line_pos}
                stopPropagation
              />
              <div className="mt-0.5 truncate text-slate-500">
                {relationshipEvidenceTitle(item)}
              </div>
            </div>
            <span className="text-slate-500">
              {relationshipEvidenceAmount(item.evidence_spend)}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-slate-500">
            {[item.reason, item.source_summary].filter(Boolean).join(" · ")}
          </div>
        </div>
      ))}
    </div>
  );
}

function relationshipEvidenceTitle(item: CostCenterRelationshipEvidence): string {
  const doc = item.source_doc_number || item.source_doc_id || "Beleg";
  const pos = item.source_line_pos ? `Pos. ${item.source_line_pos}` : null;
  const source = item.source_kind || "Quelle";
  return [source, doc, pos, item.source_date].filter(Boolean).join(" · ");
}

function relationshipEvidenceAmount(value: string | number | null): string {
  if (value === null || value === undefined || value === "") return "";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return amount.toLocaleString("de-AT", {
    style: "currency",
    currency: "EUR",
  });
}
